import { useEffect, useMemo, useState } from "react";
import { useRouteLoaderData } from "@remix-run/react";
import { useGreaseGrowthbookFeatureValue } from "@made-people/grease-growthbook";
import {
  useProductBase,
  useRelatedProducts,
} from "@made-people/grease-product";
import { useGeneralSettings } from "../../../hooks/use-general-settings";
import { mmRootLoader } from "../../../root-loader/mm-root-loader";
import { parseComfortSuiteSettings } from "../settings";
import { FeatureToggle } from "./feature-toggle";
import type { RelatedProduct } from "@made-people/grease-product";

type FeatureToggleConfig = {
  key: string;
  relationType: string;
  featureOptions: [string, string];
  productProperty: string;
  activeOptionProperty: string;
  title: string;
  enabled: boolean | undefined;
};

const getRelatedProduct = (
  relatedProducts: RelatedProduct[],
  relationType: string,
) => {
  return relatedProducts.find(
    (relatedProduct) => relatedProduct.relationType === relationType,
  )?.product;
};

export const FeatureToggles = () => {
  const rootLoaderData = useRouteLoaderData<typeof mmRootLoader>("root");
  const hasServerPayload = Boolean(rootLoaderData?.gbState?.p);
  const [mounted, setMounted] = useState(false);
  const productBase = useProductBase();
  const relatedProducts = useRelatedProducts();
  const generalSettings = useGeneralSettings();
  const { stylePickerGroups, featureTogglesExclude } = useMemo(
    () => parseComfortSuiteSettings(generalSettings),
    [generalSettings],
  );
  const customisationActive = useMemo(() => {
    if (!stylePickerGroups.length) {
      return false;
    }
    return stylePickerGroups.some((group) => {
      if (!group.categoryPage) {
        return false;
      }
      return productBase.categories.some(
        (category) => category.slug === group.categoryPage,
      );
    });
  }, [productBase.categories, stylePickerGroups]);

  const isInExcludedCategory = useMemo(() => {
    if (!featureTogglesExclude.length) {
      return false;
    }
    return productBase.categories.some((category) =>
      featureTogglesExclude.includes(category.slug),
    );
  }, [productBase.categories, featureTogglesExclude]);

  const wiringToggleEnabled = useGreaseGrowthbookFeatureValue(
    "pdp:wiring-toggle",
    false,
  );
  const closureToggleEnabled = useGreaseGrowthbookFeatureValue(
    "pdp:closure-toggle",
    false,
  );
  const laceToggleEnabled = useGreaseGrowthbookFeatureValue(
    "pdp:lace-toggle",
    false,
  );
  const shapingToggleEnabled = useGreaseGrowthbookFeatureValue(
    "pdp:shaping-toggle",
    false,
  );
  const sideWingToggleEnabled = useGreaseGrowthbookFeatureValue(
    "pdp:side-wing-toggle",
    false,
  );

  const configs = useMemo<FeatureToggleConfig[]>(
    () => [
      {
        key: "sidewing",
        relationType: "sidewing-variant",
        featureOptions: ["Sidewing", "original"],
        productProperty: "sidewing",
        activeOptionProperty: "hasSideWing",
        title: "Toggle_Choose_SideWing_Title",
        enabled: sideWingToggleEnabled,
      },
      {
        key: "lace",
        relationType: "lace-variant",
        featureOptions: ["lace", "no-lace"],
        productProperty: "material_aspect",
        activeOptionProperty: "hasLace",
        title: "Toggle_Choose_Lace_Title",
        enabled: laceToggleEnabled,
      },
      {
        key: "shaping",
        relationType: "shaping-variant",
        featureOptions: ["smoothing", "original"],
        productProperty: "shaping",
        activeOptionProperty: "isLightShaping",
        title: "Toggle_Choose_Shaping_Title",
        enabled: shapingToggleEnabled,
      },
      {
        key: "wiring",
        relationType: "wiring-variant",
        featureOptions: ["wired", "non-wired"],
        productProperty: "wiring",
        activeOptionProperty: "isWired",
        title: "Toggle_Choose_Wiring_Title",
        enabled: wiringToggleEnabled,
      },
      {
        key: "closure",
        relationType: "closure-variant",
        featureOptions: ["back-closure", "front-closure"],
        productProperty: "closure",
        activeOptionProperty: "hasBackClosure",
        title: "Toggle_Choose_Closure_Title",
        enabled: closureToggleEnabled,
      },
    ],
    [
      closureToggleEnabled,
      laceToggleEnabled,
      shapingToggleEnabled,
      sideWingToggleEnabled,
      wiringToggleEnabled,
    ],
  );

  const enabledConfigs = configs.filter((config) => config.enabled);
  useEffect(() => {
    setMounted(true);
  }, []);

  if (!hasServerPayload && !mounted) {
    return null;
  }

  if (customisationActive || isInExcludedCategory) {
    return null;
  }

  if (!enabledConfigs.length) {
    return null;
  }

  return (
    <>
      {enabledConfigs.map((config) => {
        const relatedProduct = getRelatedProduct(
          relatedProducts,
          config.relationType,
        );
        if (!relatedProduct) {
          return null;
        }
        return (
          <FeatureToggle
            key={config.key}
            productBase={productBase}
            relatedProduct={relatedProduct}
            featureOptions={config.featureOptions}
            productProperty={config.productProperty}
            activeOptionProperty={config.activeOptionProperty}
            title={config.title}
          />
        );
      })}
    </>
  );
};
